// The counterparty register (TX-US-0016): every counterparty, searchable by name and filtered by
// category and, under Logistics or Service provider, subcategory (TX-US-0041), commodity, country
// (TX-US-0041), status and rating (TX-US-0040). The sort, the filters and the page live in the URL, as on the
// deal register, so a view is a link and the back button walks through paging.
//
// Everyone reads it; whoever adds counterparties (ADR-0014) sees Add here and Edit on the record.
// The API refuses anyone else's write regardless - hiding a control is not authorisation.

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { pageCounterparties } from '../api.js';
import { useMe } from '../MeContext.js';
import { COUNTERPARTY_EDITOR_ROLES, holdsAny } from '../roles.js';
import Icon from '../ui/Icon.jsx';
import Pager from '../ui/Pager.jsx';
import SearchFilter from '../ui/SearchFilter.jsx';
import useMediaQuery from '../ui/useMediaQuery.js';
import usePageTrail from '../ui/usePageTrail.js';
import Combobox from '../ui/Combobox.jsx';
import { COUNTRY_OPTIONS } from './CounterpartyFields.jsx';
import CounterpartyTable from './CounterpartyTable.jsx';
import CounterpartyTiles from './CounterpartyTiles.jsx';
import useProducts from './useProducts.js';
import {
  fromSearchParams,
  nextSort,
  toApiSearch,
  toQueryString,
  withCategory,
  withCursor,
  withFilter,
} from './counterpartyUrl.js';
import { CATEGORIES, STATUSES, subcategoriesOf } from './labels.js';
import { RATING_FILTERS } from './ratings/ratings.js';

const PAGE_SIZE = 25;
const TILES_BELOW = '(max-width: 60rem)';

export default function CounterpartyRegister() {
  const me = useMe();
  const canWrite = holdsAny(me, COUNTERPARTY_EDITOR_ROLES);

  const [searchParams, setSearchParams] = useSearchParams();
  const query = fromSearchParams(searchParams);
  const tiles = useMediaQuery(TILES_BELOW);

  const [page, setPage] = useState(null);
  const { products } = useProducts();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);

  const apply = useCallback(
    (next, { replace = false } = {}) =>
      setSearchParams(new URLSearchParams(toQueryString(next)), { replace }),
    [setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);

    pageCounterparties(toApiSearch(fromSearchParams(searchParams), PAGE_SIZE))
      .then((result) => {
        if (!cancelled) setPage(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const paging = usePageTrail({ query, page, apply, withCursor });

  const onSort = (key) => {
    paging.reset();
    apply(nextSort(query, key));
  };

  const onFilter = (name, value, options) => {
    paging.reset();
    apply(withFilter(query, name, value), options);
  };

  const onCategory = (value) => {
    paging.reset();
    apply(withCategory(query, value));
  };

  const subcategories = subcategoriesOf(query.category);
  const rows = page?.counterparties ?? [];

  return (
    <section className="register counterparties">
      <header className="register-header">
        <h2>Counterparties</h2>
        {canWrite && (
          <Link className="icon-button primary" to="/counterparties/new"
                aria-label="Add counterparty" title="Add counterparty">
            <Icon name="plus" />
          </Link>
        )}
      </header>

      <div className="filters">
        <SearchFilter
          label="Name"
          placeholder="any part of the name"
          value={query.name}
          onCommit={(value, options) => onFilter('name', value, options)}
        />
        <label>
          Category
          <select value={query.category ?? ''} onChange={(e) => onCategory(e.target.value)}>
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </label>
        {subcategories.length > 0 && (
          <label>
            Subcategory
            <select value={query.subcategory ?? ''} onChange={(e) => onFilter('subcategory', e.target.value)}>
              <option value="">All</option>
              {subcategories.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          </label>
        )}
        <label>
          Commodity
          <select value={query.commodity ?? ''} onChange={(e) => onFilter('commodity', e.target.value)}>
            <option value="">All</option>
            {products.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
          </select>
        </label>
        <div className="filter-combobox">
          <span className="field-label">Country</span>
          <Combobox label="Country" options={COUNTRY_OPTIONS} value={query.country ?? ''}
                    onChange={(value) => onFilter('country', value ?? '')} placeholder="Any country" />
        </div>
        <label>
          Status
          <select value={query.status ?? ''} onChange={(e) => onFilter('status', e.target.value)}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </label>
        <label>
          Rating
          <select value={query.rating ?? ''} onChange={(e) => onFilter('rating', e.target.value)}>
            <option value="">All</option>
            {RATING_FILTERS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </label>
      </div>

      {error && <p className="error">{error}</p>}

      {page && (tiles
        ? <CounterpartyTiles rows={rows} products={products} query={query} onSort={onSort} />
        : <CounterpartyTable rows={rows} products={products} query={query} onSort={onSort} />)}

      {busy && <p className="muted">Loading…</p>}

      {!busy && rows.length === 0 && (
        <p className="empty">
          No counterparties here.
          {canWrite && <> <Link to="/counterparties/new">Add the first one</Link>.</>}
        </p>
      )}

      <Pager
        busy={busy}
        hasPrevious={paging.hasPrevious}
        hasMore={Boolean(page?.has_more)}
        onPrevious={paging.onPrevious}
        onNext={paging.onNext}
      />
    </section>
  );
}
